import { useCallback, useRef, useState } from 'react';

/**
 * useClickLock — «однокликовая» защита действий в UI.
 *
 * Назначение:
 * - Предотвращает повторные клики/запуски операции, пока она выполняется.
 * - Используйте `isLocked`, чтобы отключать кнопку и/или показывать загрузку.
 *
 * API:
 * - isLocked: boolean — текущее состояние блокировки (для UI).
 * - tryLock(): boolean — попытка установить блокировку; вернёт false, если уже залочено.
 * - unlock(): void — снять блокировку.
 * - runLocked(fn): void — установить блокировку и выполнить fn один раз.
 *   - Автоматически снимает блокировку при синхронной ошибке или отклонённом промисе.
 *   - НЕ снимает блокировку при успешном выполнении (обычно происходит редирект/размонтаж; иначе вызовите unlock() вручную).
 *
 * Примеры:
 * - Асинхронный сценарий (Promise):
 *   runLocked(async () => {
 *     await doRequest();
 *     // далее редирект ИЛИ unlock(), если нужно дать повторную попытку без редиректа
 *   });
 *
 * - Колбэки (например, mutate):
 *   runLocked(() => mutate(payload, { onError: unlock }));
 */

export const useClickLock = () => {
  const lockRef = useRef(false);
  const [isLocked, setIsLocked] = useState(false);

  const tryLock = useCallback(() => {
    if (lockRef.current) {
      return false;
    }

    lockRef.current = true;
    setIsLocked(true);

    return true;
  }, []);

  const unlock = useCallback(() => {
    lockRef.current = false;
    setIsLocked(false);
  }, []);

  const runLocked = useCallback(
    <T>(fn: () => Promise<T> | void) => {
      if (!tryLock()) {
        return;
      }

      try {
        const maybePromise = fn();

        if (maybePromise && typeof (maybePromise as unknown as Promise<unknown>).then === 'function') {
          (maybePromise as Promise<unknown>).catch(() => {
            unlock();
          });
        }
      } catch {
        unlock();
      }
    },
    [tryLock, unlock],
  );

  return { isLocked, tryLock, unlock, runLocked };
};
